import React from "react"
import PropTypes from "prop-types"
import i18n from "i18n"
import {
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  Select,
  TextField,
  Typography,
} from "@mui/material"
import {
  ChevronLeftOutlined,
  FilterAltOffOutlined,
  TuneOutlined,
} from "@mui/icons-material"

// Selección única, no múltiple: `GET /cype/search` sólo acepta un valor en `section`
// (un `term` de OpenSearch). Mandar varios no filtra por varios — devuelve 0 resultados
// con CSV, y con `section[]=` el backend ignora el filtro y responde el catálogo entero.
// Para permitir multi-selección haría falta que scrap-cype emitiera un `terms`.
const ALL_SOURCES = ""
const ALL_UNITS = ""

// `hidden` marca tipos que aún no existen como sección filtrable. Se mantienen aquí
// (y se filtran al renderizar) para reactivarlos quitando el flag.
const TYPES = [
  { value: ALL_SOURCES, label: i18n.t("Todas las fuentes") },
  { value: "Obra nueva", label: i18n.t("Obra nueva") },
  { value: "Rehabilitación", label: i18n.t("Rehabilitación") },
  { value: "Espacios urbanos", label: i18n.t("Espacios urbanos") },
  // OCULTAS — pendientes de backend: no son secciones del catálogo CYPE, son fuentes de
  // datos propias que todavía no existen. "Mi banco de precios" necesita el banco por
  // organización (con auth) en el backend de planhopper, y "Planhopper verificadas" un
  // catálogo curado. Ninguna de las dos tiene valor válido para `?section=`.
  {
    value: "Mi banco de precios",
    label: i18n.t("Mi banco de precios"),
    disabled: true,
    badge: i18n.t("Pronto"),
    hidden: true,
  },
  {
    value: "Planhopper verificadas",
    label: i18n.t("Planhopper verificadas"),
    disabled: true,
    badge: i18n.t("Pronto"),
    hidden: true,
  },
]

const DIVIDER = "rgba(0,0,0,0.12)"

// Selección múltiple pendiente de backend: por ahora sólo CYPE tiene datos disponibles,
// así que se muestra primero, marcado y activo (pero de sólo lectura); el resto son
// fuentes futuras, deshabilitadas.
const DATA_SOURCES = [
  {
    key: "cype",
    label: i18n.t("CYPE generador de precios"),
    checked: true,
    disabled: false,
    readOnly: true,
  },
  {
    key: "mi-banco",
    label: i18n.t("Mi banco de precios"),
    checked: false,
    disabled: true,
  },
  {
    key: "mis-presupuestos",
    label: i18n.t("Mis presupuestos"),
    checked: false,
    disabled: true,
  },
  {
    key: "banco-planhopper",
    label: i18n.t("Banco Planhopper"),
    checked: false,
    disabled: true,
  },
]

const DataSourceFilters = () => (
  <Paper variant="outlined" sx={{ borderRadius: 2, mb: 1, overflow: "hidden" }}>
    <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
      <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
        {i18n.t("Fuentes")}
      </Typography>
    </Box>
    {DATA_SOURCES.map(option => (
      <Box
        key={option.key}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.5,
          py: 1,
        }}
      >
        <Checkbox
          checked={option.checked}
          disabled={option.disabled}
          size="small"
          sx={{ p: 0 }}
          onChange={() => {}}
          inputProps={option.readOnly ? { readOnly: true } : undefined}
        />
        <Typography
          sx={{
            flex: 1,
            fontSize: 14,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: option.disabled ? "rgba(0,0,0,0.38)" : "inherit",
          }}
        >
          {option.label}
        </Typography>
        {option.disabled && (
          <Chip
            label={i18n.t("Pronto")}
            size="small"
            sx={{
              height: 20,
              fontSize: 11,
              bgcolor: "rgba(0,0,0,0.08)",
              color: "rgba(0,0,0,0.38)",
            }}
          />
        )}
      </Box>
    ))}
  </Paper>
)

const TypeFilters = ({ type, onChange }) => (
  <Paper variant="outlined" sx={{ borderRadius: 2, mb: 1, overflow: "hidden" }}>
    <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
      <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
        {i18n.t("Tipo")}
      </Typography>
    </Box>
    {TYPES.filter(({ hidden }) => !hidden).map(option => (
      <Box
        key={option.label}
        onClick={() => !option.disabled && onChange(option.value)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.5,
          py: 1,
          cursor: option.disabled ? "default" : "pointer",
          opacity: option.disabled ? 0.5 : 1,
          filter: option.disabled ? "grayscale(1)" : "none",
          pointerEvents: option.disabled ? "none" : "auto",
          "&:hover": {
            bgcolor: option.disabled ? "transparent" : "rgba(0,0,0,0.04)",
          },
        }}
      >
        <Radio
          checked={type === option.value}
          disabled={option.disabled}
          size="small"
          sx={{ p: 0, color: "#222" }}
          onChange={() => {}}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{ display: "flex", gap: 1, alignItems: "center", minWidth: 0 }}
          >
            <Typography
              sx={{
                fontSize: 14,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {option.label}
            </Typography>
            {option.badge && (
              <Chip
                label={option.badge}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: 11 }}
              />
            )}
          </Box>
        </Box>
      </Box>
    ))}
  </Paper>
)

TypeFilters.propTypes = {
  type: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
}

// La sección se mantiene visible sin resultados para que el panel no cambie de altura al
// buscar; sólo el chip "Todas" está presente porque las unidades se descubren del facet.
const UnitFilters = ({ unit, units, onChange }) => (
  <Paper variant="outlined" sx={{ borderRadius: 2, mb: 1, overflow: "hidden" }}>
    <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
      <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
        {i18n.t("Unidad")}
      </Typography>
    </Box>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, p: 1.5 }}>
      <Chip
        label={i18n.t("Todas")}
        size="small"
        variant={unit === ALL_UNITS ? "filled" : "outlined"}
        onClick={() => onChange(ALL_UNITS)}
        sx={{
          fontSize: 12,
          fontWeight: 700,
          bgcolor: unit === ALL_UNITS ? "#222" : "transparent",
          color: unit === ALL_UNITS ? "#fff" : "#222",
          "&:hover": {
            bgcolor: unit === ALL_UNITS ? "#222" : "rgba(0,0,0,0.04)",
          },
        }}
      />
      {units.map(option => (
        <Chip
          key={option.key}
          label={`${option.key} · ${option.count}`}
          size="small"
          variant={unit === option.key ? "filled" : "outlined"}
          onClick={() => onChange(option.key)}
          sx={{
            fontSize: 12,
            fontWeight: 700,
            bgcolor: unit === option.key ? "#222" : "transparent",
            color: unit === option.key ? "#fff" : "#222",
            "&:hover": {
              bgcolor: unit === option.key ? "#222" : "rgba(0,0,0,0.04)",
            },
          }}
        />
      ))}
    </Box>
  </Paper>
)

UnitFilters.propTypes = {
  unit: PropTypes.string.isRequired,
  units: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
    }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
}

const PriceFilters = ({ minPrice, maxPrice, onMinChange, onMaxChange }) => {
  // El backend acepta min > max y responde 200 con 0 resultados, así que el aviso es la
  // única forma de distinguirlo de "no hay partidas con ese texto".
  const invalid =
    minPrice !== "" && maxPrice !== "" && Number(minPrice) > Number(maxPrice)
  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: 2, mb: 1, overflow: "hidden" }}
    >
      <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
        <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
          {i18n.t("Precio unitario (€)")}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.5 }}>
        <TextField
          value={minPrice}
          onChange={e => onMinChange(e.target.value)}
          placeholder={i18n.t("Mín.")}
          size="small"
          type="number"
          error={invalid}
          inputProps={{
            min: 0,
            step: "any",
            "aria-label": i18n.t("Precio mínimo"),
          }}
          sx={{ flex: 1, "& input": { fontSize: 13 } }}
        />
        <Typography sx={{ fontSize: 13, color: "#4f4f4f" }}>—</Typography>
        <TextField
          value={maxPrice}
          onChange={e => onMaxChange(e.target.value)}
          placeholder={i18n.t("Máx.")}
          size="small"
          type="number"
          error={invalid}
          inputProps={{
            min: 0,
            step: "any",
            "aria-label": i18n.t("Precio máximo"),
          }}
          sx={{ flex: 1, "& input": { fontSize: 13 } }}
        />
      </Box>
      {invalid && (
        <Typography sx={{ fontSize: 12, color: "#b3261e", px: 1.5, pb: 1.5 }}>
          {i18n.t("El mínimo es mayor que el máximo.")}
        </Typography>
      )}
    </Paper>
  )
}

PriceFilters.propTypes = {
  minPrice: PropTypes.string.isRequired,
  maxPrice: PropTypes.string.isRequired,
  onMinChange: PropTypes.func.isRequired,
  onMaxChange: PropTypes.func.isRequired,
}

// OCULTO — pendiente de backend: no hay más ubicaciones que España todavía. El select
// se muestra deshabilitado para anticipar el control sin ofrecer una opción sin efecto.
const LocationFilters = () => (
  <Paper variant="outlined" sx={{ borderRadius: 2, mb: 1, overflow: "hidden" }}>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: 2,
        py: 1.25,
        borderBottom: "1px solid rgba(0,0,0,0.12)",
      }}
    >
      <Typography
        sx={{ fontWeight: 800, fontSize: 14, color: "rgba(0,0,0,0.38)" }}
      >
        {i18n.t("Ubicación")}
      </Typography>
      <Chip
        label={i18n.t("Pronto")}
        size="small"
        sx={{
          height: 20,
          fontSize: 11,
          bgcolor: "rgba(0,0,0,0.08)",
          color: "rgba(0,0,0,0.38)",
        }}
      />
    </Box>
    <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
      <Box>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: "rgba(0,0,0,0.38)",
            mb: 0.5,
          }}
        >
          {i18n.t("País")}
        </Typography>
        <Select
          value="ES"
          disabled
          size="small"
          fullWidth
          sx={{ fontSize: 13 }}
        >
          <MenuItem value="ES">{i18n.t("España")}</MenuItem>
        </Select>
      </Box>
      <Box>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: "rgba(0,0,0,0.38)",
            mb: 0.5,
          }}
        >
          {i18n.t("Provincia")}
        </Typography>
        <Select
          value=""
          disabled
          displayEmpty
          size="small"
          fullWidth
          sx={{ fontSize: 13 }}
          renderValue={() => i18n.t("Seleccionar provincia")}
        >
          <MenuItem value="">{i18n.t("Seleccionar provincia")}</MenuItem>
        </Select>
      </Box>
    </Box>
  </Paper>
)

// OCULTO — pendiente de backend: `GET /cype/chapters` ya permite NAVEGAR la taxonomía
// (3 niveles, con títulos legibles y contadores), pero no se puede FILTRAR por capítulo:
// `chapter_codes` no está en `filter_fields` de scrap-cype, así que el param se ignora en
// silencio y devuelve el catálogo entero. Se muestra deshabilitado para anticipar el
// control sin ofrecer una opción sin efecto.
// Para reactivar: añadir `chapter_codes` a `filter_fields` (schema.py) y un param
// `?chapter=` en `_parse_filters` (handlers/search.py) — el campo ya está indexado como
// keyword con el breadcrumb completo, así que un `term` sobre "HR" captura sus
// descendientes y no hace falta reindexar.
const CATEGORIES = [
  { code: "0", label: i18n.t("Actuaciones previas") },
  { code: "D", label: i18n.t("Demoliciones") },
  { code: "A", label: i18n.t("Acondicionamiento del terreno") },
  { code: "C", label: i18n.t("Cimentaciones") },
  { code: "E", label: i18n.t("Estructuras") },
  { code: "I", label: i18n.t("Instalaciones") },
  { code: "R", label: i18n.t("Revestimientos y trasdosados") },
]

const CategoryFilters = () => (
  <Paper variant="outlined" sx={{ borderRadius: 2, mb: 1, overflow: "hidden" }}>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: 2,
        py: 1.25,
        borderBottom: "1px solid rgba(0,0,0,0.12)",
      }}
    >
      <Typography
        sx={{ fontWeight: 800, fontSize: 14, color: "rgba(0,0,0,0.38)" }}
      >
        {i18n.t("Tipología y categorías")}
      </Typography>
      <Chip
        label={i18n.t("Pronto")}
        size="small"
        sx={{
          height: 20,
          fontSize: 11,
          bgcolor: "rgba(0,0,0,0.08)",
          color: "rgba(0,0,0,0.38)",
        }}
      />
    </Box>
    <Box sx={{ px: 1.5, pt: 1.5 }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          color: "rgba(0,0,0,0.38)",
          mb: 0.5,
        }}
      >
        {i18n.t("Tipología de obra")}
      </Typography>
      <Select value="all" disabled size="small" fullWidth sx={{ fontSize: 13 }}>
        <MenuItem value="all">{i18n.t("Todas")}</MenuItem>
      </Select>
    </Box>
    <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
      <Typography
        sx={{ fontSize: 12, fontWeight: 700, color: "rgba(0,0,0,0.38)" }}
      >
        {i18n.t("Categorías")}
      </Typography>
    </Box>
    <Box sx={{ pb: 0.5 }}>
      {CATEGORIES.map(option => (
        <Box
          key={option.code}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: 1.5,
            py: 0.5,
          }}
        >
          <Checkbox disabled size="small" sx={{ p: 0 }} />
          <Typography
            sx={{
              fontSize: 14,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "rgba(0,0,0,0.38)",
            }}
          >
            {option.code} {option.label}
          </Typography>
        </Box>
      ))}
    </Box>
  </Paper>
)

const PartBrowserFilters = ({
  source,
  onSourceChange,
  unit,
  units,
  onUnitChange,
  minPrice,
  maxPrice,
  onMinPriceChange,
  onMaxPriceChange,
  onReset,
  onHide,
  collapsed,
  onShow,
}) => (
  <Box
    sx={{
      width: collapsed ? 56 : 292,
      flexShrink: 0,
      borderRight: `1px solid ${DIVIDER}`,
      bgcolor: "#fff",
      overflow: "hidden",
      transition: "width 220ms ease",
      position: "relative",
    }}
  >
    {/* Collapsed strip */}
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: 56,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        pt: 1.5,
        opacity: collapsed ? 1 : 0,
        pointerEvents: collapsed ? "auto" : "none",
        transition: "opacity 150ms ease",
      }}
    >
      <IconButton
        title={i18n.t("Mostrar filtros")}
        onClick={onShow}
        sx={{ border: `1px solid ${DIVIDER}`, borderRadius: 1.25 }}
      >
        <TuneOutlined sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>

    {/* Full content */}
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        opacity: collapsed ? 0 : 1,
        pointerEvents: collapsed ? "none" : "auto",
        transition: "opacity 150ms ease",
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          pb: 0,
          mb: 2,
        }}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 800 }}>
          {i18n.t("Filtros")}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
          <Button
            variant="text"
            size="small"
            startIcon={<FilterAltOffOutlined fontSize="small" />}
            onClick={onReset}
            sx={{ color: "#222", fontSize: 13, fontWeight: 700 }}
          >
            {i18n.t("Limpiar")}
          </Button>
          <IconButton
            size="small"
            onClick={onHide}
            title={i18n.t("Ocultar filtros")}
            sx={{ border: `1px solid ${DIVIDER}`, borderRadius: 1.25 }}
          >
            <ChevronLeftOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 2, pb: 2 }}>
        <DataSourceFilters />
        <TypeFilters type={source} onChange={onSourceChange} />
        <UnitFilters unit={unit} units={units} onChange={onUnitChange} />
        <PriceFilters
          minPrice={minPrice}
          maxPrice={maxPrice}
          onMinChange={onMinPriceChange}
          onMaxChange={onMaxPriceChange}
        />
        <CategoryFilters />
        <LocationFilters />
      </Box>
    </Box>
  </Box>
)

PartBrowserFilters.propTypes = {
  source: PropTypes.string.isRequired,
  onSourceChange: PropTypes.func.isRequired,
  unit: PropTypes.string.isRequired,
  units: PropTypes.array.isRequired,
  onUnitChange: PropTypes.func.isRequired,
  minPrice: PropTypes.string.isRequired,
  maxPrice: PropTypes.string.isRequired,
  onMinPriceChange: PropTypes.func.isRequired,
  onMaxPriceChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onHide: PropTypes.func.isRequired,
  collapsed: PropTypes.bool,
  onShow: PropTypes.func,
}

PartBrowserFilters.defaultProps = {
  collapsed: false,
  onShow: null,
}

export default PartBrowserFilters
